import type { TournamentPlayersSchema } from "@teamz/common";

import AddIcon from "@/assets/add.svg";
import EditIcon from "@/assets/edit.svg";
import DotsIcon from "@/assets/placeholder-dots.svg";
import TournamentIcon from "@/assets/tournaments.svg";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { playerFFTranking, rankingTypes, tournamentPlayersSchema } from "@teamz/common";
import { type ComponentProps, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { RouterOutputs } from "@/lib/trpc";

import { useMe } from "@/context/user-context";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";

import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Sheet, SheetClose, SheetContent, SheetFooter, SheetForm, SheetHeader, SheetTitle } from "../ui/sheet";
import { ErrorField, RootField, TextField } from "../ui/text-field";

const EmptyButton = ({ gender, ...props }: ComponentProps<"button"> & { gender: "male" | "female" }) => {
  const { user } = useMe();

  return (
    <button
      type="button"
      className={cn(
        "h-16 w-full rounded-lg bg-purple-200 p-1",
        gender === "male" && "bg-blue-200",
        gender === "female" && "bg-purple-200",
        user.role === "player" && "pointer-events-none",
      )}
      {...props}
    >
      <div
        className={cn(
          "flex h-full items-center justify-center rounded-md border border-dashed border-neutral-400 bg-white",
          user.role === "player" && "pointer-events-none border-0",
        )}
      >
        {user.role === "club"
          ? <AddIcon className="text-orange-500" />
          : <DotsIcon />}
      </div>
    </button>
  );
};

const TournamentPlayersButton = ({ tournamentPlayers, variant, ...props }: { tournamentPlayers: RouterOutputs["tournament"]["getTournament"]["tournamentPlayers"][number]; variant: "blue" | "purple" } & ComponentProps<"button">) => {
  const { t } = useTranslation(["profile"]);
  const { user } = useMe();

  return (
    <button
      type="button"
      className={cn(
        "flex h-16 w-full items-center gap-1 rounded-lg p-1",
        variant === "blue" && "bg-blue-200",
        variant === "purple" && "bg-purple-200",
        user.role === "player" && "pointer-events-none",
      )}
      {...props}
    >
      <div className="relative flex h-full flex-1 items-center justify-center rounded-md bg-white">
        <span className="text-sm font-bold text-blue-500">
          {
            (tournamentPlayers.rankingType === "FFT" && tournamentPlayers.ranking === "T100")
              ? `N${tournamentPlayers.rankingNumber}`
              : tournamentPlayers.ranking
          }
        </span>

        <div
          className={cn(
            "absolute left-0 top-0 rounded-br px-1.5 text-xs font-medium",
            variant === "blue" && "bg-blue-200 text-blue-500",
            variant === "purple" && "bg-purple-200 text-purple-500",
          )}
        >
          {tournamentPlayers.rankingType}
        </div>
        <div
          className={cn(
            "absolute bottom-0 right-0 text-nowrap rounded-tl px-1.5 text-xs font-medium",
            variant === "blue" && "bg-blue-200 text-blue-500",
            variant === "purple" && "bg-purple-200 text-purple-500",
          )}
        >
          {t("profile:or-more")}
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center">
        <span className="text-nowrap text-sm font-semibold text-blue-500">
          x
          {tournamentPlayers.number}
        </span>
      </div>

      {user.role === "club" && <EditIcon className="size-4 shrink-0 text-neutral-500 sm:block" />}
    </button>
  );
};

const PlayerWantedForm = ({ tournament, gender, tournamentPlayersId, closeModal }: { tournament: RouterOutputs["tournament"]["getTournament"]; gender: "male" | "female"; tournamentPlayersId?: string; closeModal: () => void }) => {
  const { t } = useTranslation(["common", "profile"]);
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation(trpc.tournament.upsertTournamentPlayers.mutationOptions({
    onSuccess: () => {
      closeModal();
      queryClient.invalidateQueries(trpc.tournament.getTournament.queryFilter());
    },
  }));

  const actualTournementPlayers = tournament.tournamentPlayers.filter(players => players.id === tournamentPlayersId)[0];

  const playerWantedForm = useForm<TournamentPlayersSchema>({
    resolver: zodResolver(tournamentPlayersSchema),
    defaultValues: {
      id: actualTournementPlayers?.id || undefined,
      rankingType: actualTournementPlayers?.rankingType || undefined,
      ranking: actualTournementPlayers?.ranking || undefined,
      rankingNumber: actualTournementPlayers?.rankingNumber || undefined,
      number: actualTournementPlayers?.number || undefined,
    },
  });

  const onSubmit = playerWantedForm.handleSubmit((data) => {
    mutate({ tournamentPlayersSchema: data, tournamentId: tournament.id, gender });
  });

  const { mutate: deleteTournamentPlayers, isPending: isDeleting } = useMutation(trpc.tournament.deleteTournamentPlayers.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.tournament.getTournament.queryFilter());
      closeModal();
    },
  }));

  const onDeleteTournamentPlayers = () => {
    if (!actualTournementPlayers)
      return;
    deleteTournamentPlayers({ id: actualTournementPlayers.id });
  };

  return (
    <>
      <SheetForm id="PlayerWantedForm" onSubmit={onSubmit}>
        <Controller
          control={playerWantedForm.control}
          name="rankingType"
          rules={{
            onChange: (event) => {
              if (event) {
                playerWantedForm.setValue("ranking", "");
                playerWantedForm.setValue("rankingNumber", undefined);
                playerWantedForm.clearErrors("ranking");
              }
            },
          }}
          render={({ field, fieldState }) => (
            <RootField>
              <Label>{t("common:ranking-type")}</Label>
              <Select value={field.value || undefined} onValueChange={field.onChange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {rankingTypes.map(type => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {fieldState.error && (
                // @ts-expect-error Error type on null value
                <ErrorField>{t(fieldState.error.message)}</ErrorField>
              )}
            </RootField>
          )}
        />

        {/* FFT Ranking */}
        {playerWantedForm.watch("rankingType") === "FFT"
        && (
          <div className="flex flex-col gap-4">
            <Controller
              control={playerWantedForm.control}
              name="ranking"
              rules={{
                onChange: (event) => {
                  if (event) {
                    playerWantedForm.setValue("rankingNumber", undefined);
                  }
                },
              }}
              render={({ field, fieldState }) => (
                <RootField>
                  <Label>
                    {t("profile:ranking-wanted")}
                  </Label>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {playerFFTranking.map(ranking => (
                          <SelectItem key={ranking} value={ranking}>{ranking === "T100" ? `${t("profile:numbered")} - T100` : ranking}</SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  {fieldState.error?.message && (
                    // @ts-expect-error Error type on null value
                    <ErrorField>{t(fieldState.error.message)}</ErrorField>
                  )}
                </RootField>
              )}
            />

            {playerWantedForm.watch("ranking") === "T100" && (
              <TextField
                type="number"
                helper={t("profile:ranking-numbered-helper")}
                prefix={<span className="text-sm font-bold">N</span>}
                label={t("profile:numbered")}
                max={100}
                min={1}
                {...playerWantedForm.register("rankingNumber")}
                errorMessage={
                  // @ts-expect-error Error type on null value
                  playerWantedForm.formState.errors.rankingNumber?.message ? t(playerWantedForm.formState.errors.rankingNumber.message) : undefined
                }
              />
            )}
          </div>
        )}

        {/* UTR Ranking */}
        {playerWantedForm.watch("rankingType") === "UTR" && (
          <Controller
            name="ranking"
            control={playerWantedForm.control}
            render={({ field }) => (
              <TextField
                helper="ex: 12.34, 1.23"
                label={t("profile:ranking-wanted")}
                value={field.value || ""}
                onChange={(event) => {
                  field.onChange(event.target.value);
                }}
                errorMessage={
                  // @ts-expect-error Error type on null value
                  playerWantedForm.formState.errors.ranking?.message ? t(playerWantedForm.formState.errors.ranking.message) : undefined
                }
              />
            )}
          />
        )}

        {/* ATP/WTA/ITF Ranking */}
        {
          ["ATP", "WTA", "ITF"].includes(playerWantedForm.watch("rankingType"))
          && (
            <TextField
              type="number"
              helper={t("profile:ranking-ATP-WTA-ITF-helper")}
              label={t("profile:ranking-wanted")}
              max={2000}
              min={1}
              {...playerWantedForm.register("ranking")}
              errorMessage={
                // @ts-expect-error Error type on null value
                playerWantedForm.formState.errors.ranking?.message ? t(playerWantedForm.formState.errors.ranking.message) : undefined
              }
            />
          )
        }

        {
          !playerWantedForm.watch("rankingType")
          && (
          // Need this disabled field to act like a placeholder when rankingType is not selected yet
            <TextField
              label={t("profile:ranking-wanted")}
              readOnly
              className="pointer-events-none"
              disabled
              errorMessage={
                // @ts-expect-error Error type on null value
                playerWantedForm.formState.errors.ranking?.message ? t(playerWantedForm.formState.errors.ranking.message) : undefined
              }
            />
          )
        }

        <TextField
          type="number"
          label="Nombre de joueurs recherchés"
          min={0}
          {...playerWantedForm.register("number")}
          disabled={!playerWantedForm.watch("ranking")}
          errorMessage={
            // @ts-expect-error Error type on null value
            playerWantedForm.formState.errors.number?.message ? t(playerWantedForm.formState.errors.number.message) : undefined
          }
        />

      </SheetForm>

      {actualTournementPlayers && (
        <Button
          type="button"
          variant="ghost"
          className="text-white"
          onClick={onDeleteTournamentPlayers}
        >
          {t("common:delete")}
        </Button>
      )}

      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="PlayerWantedForm" loading={isPending || isDeleting || playerWantedForm.formState.isSubmitting}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
};

export function TournamentPlayers({ tournament }: { tournament: RouterOutputs["tournament"]["getTournament"] }) {
  const { t } = useTranslation(["common", "profile"]);
  const [modalState, updateModalState] = useState<"men" | "women" | "closed">("closed");
  const [gender, setGender] = useState<"male" | "female">("male");
  const { user } = useMe();
  const isModalOpen = modalState !== "closed";
  const [tournamentPlayersId, setTournamentPlayersId] = useState("");

  const menPlayers = tournament.tournamentPlayers.filter(players => players.gender === "male")[0];
  const womenPlayers = tournament.tournamentPlayers.filter(players => players.gender === "female")[0];

  return (
    <>
      <div className="flex w-full flex-col gap-1">
        <div className="flex w-full flex-col gap-1 rounded-xl border border-neutral-400 p-1.5">
          <div className="flex h-9 w-full items-center justify-between px-2">
            <div className="flex h-9 items-center gap-1 text-neutral-900">
              <TournamentIcon />
              <span className="text-sm font-medium">{t("common:desktop-nav.tournaments")}</span>
              <div className="flex items-center gap-1">
                <Badge variant="blue" size="compact">{t("common:man").charAt(0)}</Badge>
                <span>/</span>
                <Badge variant="purple" size="compact">{t("common:woman").charAt(0)}</Badge>
              </div>
            </div>

            <span className="text-sm font-medium text-orange-500">{t("profile:players-wanted")}</span>
          </div>

          <div className="flex items-center gap-1.5">
            {
              menPlayers
                ? (
                    <TournamentPlayersButton
                      tournamentPlayers={menPlayers}
                      variant="blue"
                      onClick={() => {
                        updateModalState("men");
                        setTournamentPlayersId(menPlayers.id);
                        setGender("male");
                      }}
                    />
                  )
                : (
                    <EmptyButton
                      gender="male"
                      onClick={() => {
                        updateModalState("men");
                        setGender("male");
                      }}
                    />
                  )
            }
            {
              womenPlayers
                ? (
                    <TournamentPlayersButton
                      tournamentPlayers={womenPlayers}
                      variant="purple"
                      onClick={() => {
                        updateModalState("women");
                        setTournamentPlayersId(womenPlayers.id);
                        setGender("female");
                      }}
                    />
                  )
                : (
                    <EmptyButton
                      gender="female"
                      onClick={() => {
                        updateModalState("women");
                        setGender("female");
                      }}
                    />
                  )
            }
          </div>
        </div>
      </div>

      {user.role === "club"
      && (
        <Sheet
          open={isModalOpen}
          onOpenChange={() => {
            updateModalState("closed");
            setTournamentPlayersId("");
          }}
        >
          <SheetContent>
            <SheetHeader>
              <SheetTitle>
                {isModalOpen && t(`common:${modalState}`)}
              </SheetTitle>
            </SheetHeader>

            <PlayerWantedForm
              tournament={tournament}
              tournamentPlayersId={tournamentPlayersId}
              gender={gender}
              closeModal={() => {
                updateModalState("closed");
                setTournamentPlayersId("");
              }}
            />

          </SheetContent>
        </Sheet>
      )}
    </>
  );
}
